import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Swords, ArrowLeft, Trophy, Crown } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

const Row = ({ r, retired }) => (
  <Link
    to={`/g/${r.id}`}
    data-testid={`hall-row-${r.id}`}
    className="grid grid-cols-[2.5rem_1fr_auto] items-center gap-3 px-4 py-2.5 border-b border-zinc-800/60 hover:bg-amber-950/10 transition-colors group"
  >
    <span className="font-cinzel text-sm text-amber-500/70 tabular-nums">{r.rank}</span>
    <span className="min-w-0">
      <span className="block text-zinc-100 group-hover:text-amber-300 transition-colors truncate font-medium">{r.name}</span>
      <span className="block text-[11px] text-zinc-500 truncate">{r.cls} · {r.owner_name}'s Ludus</span>
    </span>
    <span className="text-right">
      <span className="block font-cinzel text-amber-400 tabular-nums">{retired ? r.best_rating : r.rating}</span>
      <span className="block text-[11px] text-zinc-500 tabular-nums">{r.wins}W–{r.losses}L</span>
    </span>
  </Link>
);

export default function Hall() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  usePageMeta({
    title: "Hall of Fame — Top Gladiators | Lanista Manager",
    description: "The highest-rated gladiators and retired legends of Lanista Manager, the free online gladiator management game. Browse champions, view their records, and build your own ludus.",
    canonical: typeof window !== "undefined" ? `${window.location.origin}/hall` : undefined,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Hall of Fame — Top Gladiators",
      "description": "Top-rated and legendary gladiators on Lanista Manager.",
    },
  });

  useEffect(() => {
    api.get("/public/hall").then((r) => setData(r.data)).catch(() => setData({ active: [], legends: [] }));
  }, []);

  return (
    <div data-testid="hall-page" className="min-h-screen bg-zinc-950 flex flex-col">
      <header className="border-b border-amber-900/30 bg-zinc-900/40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button data-testid="hall-back-btn" onClick={() => navigate("/")}
            className="flex items-center gap-2 text-zinc-400 hover:text-amber-300 transition-colors text-sm">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-2.5">
            <Swords className="text-amber-500" size={18} />
            <span className="font-cinzel text-sm tracking-[0.25em] uppercase text-amber-300">Lanista — Gladiator Manager</span>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <h1 className="font-cinzel text-3xl sm:text-5xl uppercase tracking-wide text-amber-300 mb-3">Hall of Fame</h1>
          <p className="text-zinc-400 text-base max-w-2xl mb-10">
            The greatest gladiators in the arena — the highest-rated champions fighting today and the
            legends who earned their place in history. Click any fighter to view their full record.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <section data-testid="hall-active" className="border border-amber-900/30 rounded-sm bg-zinc-900/30">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-900/40">
                <Crown className="text-amber-400" size={16} />
                <h2 className="font-cinzel uppercase tracking-widest text-amber-200 text-sm">Reigning Champions</h2>
              </div>
              {data === null ? <p className="p-4 text-zinc-500 text-sm">Loading…</p>
                : data.active.length === 0 ? <p className="p-4 text-zinc-500 text-sm">No champions yet.</p>
                : data.active.map((r) => <Row key={r.id} r={r} />)}
            </section>

            <section data-testid="hall-legends" className="border border-amber-900/30 rounded-sm bg-zinc-900/30">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-900/40">
                <Trophy className="text-amber-400" size={16} />
                <h2 className="font-cinzel uppercase tracking-widest text-amber-200 text-sm">Retired Legends</h2>
              </div>
              {data === null ? <p className="p-4 text-zinc-500 text-sm">Loading…</p>
                : data.legends.length === 0 ? <p className="p-4 text-zinc-500 text-sm">No legends yet.</p>
                : data.legends.map((r) => <Row key={r.id} r={r} retired />)}
            </section>
          </div>

          <div className="mt-12 text-center">
            <button data-testid="hall-play-btn" onClick={() => navigate("/login")}
              className="inline-flex items-center gap-2 px-8 py-3 bg-amber-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-amber-400 rounded-sm transition-colors">
              <Swords size={16} /> Build Your Own Ludus — Play Free
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
